'use client';
import { useMemo } from 'react';
import { Address, zipTokenIds } from 'nftx.js';
import { SwapType } from '@/domain/swap';
import useSwapState from '@/application/read/swap/useSwapState';
import Swap from '../../widgets/Swap';

export interface SwapContainerProps {
  className?: string;
  inContainer?: boolean;
  withinModal?: boolean;
  buyType?: SwapType;
  buyToken?: Address;
  buyAmount?: `${number}`;
  buyTokenIds?: `${number}`[];
  buyTokenAmounts?: number[];
  buyVaultId?: string;
  buyVaultAddress?: Address;
  sellType?: SwapType;
  sellToken?: Address;
  sellAmount?: `${number}`;
  sellTokenIds?: `${number}`[];
  sellTokenAmounts?: number[];
  sellVaultId?: string;
  sellVaultAddress?: Address;
  canChangeBuyToken?: boolean;
  canChangeSellToken?: boolean;
  showBuy?: boolean;
  showSell?: boolean;
  canFlip?: boolean;
  onClose?: () => void;
  searchView?: 'wallet' | 'vault';
}

function SwapContainer({
  className,
  inContainer,
  withinModal,
  canChangeBuyToken,
  canChangeSellToken,
  showBuy,
  showSell,
  canFlip,
  ...initialValues
}: SwapContainerProps) {
  const initialBuyItems = useMemo(() => {
    if (
      !initialValues.buyTokenIds?.length ||
      !initialValues.buyVaultId ||
      !initialValues.buyVaultAddress
    ) {
      return;
    }
    return zipTokenIds(
      initialValues.buyTokenIds,
      initialValues.buyTokenAmounts
    );
  }, [
    initialValues.buyTokenAmounts,
    initialValues.buyTokenIds,
    initialValues.buyVaultAddress,
    initialValues.buyVaultId,
  ]);

  const initialSellItems = useMemo(() => {
    if (
      !initialValues.sellTokenIds?.length ||
      !initialValues.sellVaultId ||
      !initialValues.sellVaultAddress
    ) {
      return;
    }
    return zipTokenIds(
      initialValues.sellTokenIds,
      initialValues.sellTokenAmounts
    );
  }, [
    initialValues.sellTokenAmounts,
    initialValues.sellTokenIds,
    initialValues.sellVaultAddress,
    initialValues.sellVaultId,
  ]);

  const [state, dispatch] = useSwapState({
    ...initialValues,
    buyItems: initialBuyItems,
    sellItems: initialSellItems,
  });

  const props = {
    canChangeBuyToken,
    canChangeSellToken,
    canFlip,
    className,
    showBuy,
    showSell,
    inContainer,
    withinModal,
  };

  return <Swap dispatch={dispatch} {...props} {...state} />;
}

export default SwapContainer;
